import { useRef } from "react";

import { IconCaretSmallDown } from "@btnet/blueprint-icons";
import { cn } from "@btnet/utilities-core";

import { BuiButton } from "../BuiButton/BuiButton";
import { BuiDropdown } from "../BuiDropdown/BuiDropdown";
import { BuiMenu } from "../BuiMenu/BuiMenu";
import "./BuiSplitButton.less";
import type { IBuiSplitButtonProps } from "./BuiSplitButton.types";

/**
 * BuiSplitButton component - a decorative split button: a primary action button (left)
 * plus a caret button that opens a menu of secondary actions.
 *
 * This is the decorative `Bui*` layer: no analytics, hotkeys, business logic, or app
 * context. Cross-layer concerns belong in the core `BTSplitButton` wrapper.
 *
 * Behavior:
 * - When `menuItems` is absent, empty, or every item is disabled, renders a standalone
 *   primary button (parity with the legacy BTSplitButton).
 * - Otherwise renders the primary button joined to a caret trigger that opens the menu.
 *   Focus returns to the caret trigger when the menu closes (via BuiDropdown's
 *   `triggerRef`).
 * @param props - The component props.
 * @example
 * ```tsx
 * <BuiSplitButton
 *   data-testid="save-split"
 *   buttonAction={{ "data-testid": "save", content: "Save", onClick: handleSave }}
 *   menuItems={[
 *     { "data-testid": "save-new", content: "Save and new", onClick: handleSaveNew },
 *     { "data-testid": "save-close", content: "Save and close", onClick: handleSaveClose },
 *   ]}
 * />
 * ```
 */
export const BuiSplitButton = (props: IBuiSplitButtonProps) => {
  const {
    "data-testid": testid,
    buttonAction,
    menuItems,
    type = "secondary",
    className,
    disabled,
    loading = false,
    placement = "bottomRight",
    collapse = false,
    getPopupContainer,
  } = props;

  const triggerRef = useRef<HTMLElement>(null);

  // Render the caret whenever menu items exist, even if every item is disabled — the
  // menu still opens so users can see which actions would be available. Individual items
  // render disabled via their own `disabled` prop. Only a truly empty menu collapses to
  // a standalone button.
  const hasMenu = !!menuItems?.length;

  const shouldCollapse = collapse && !!buttonAction.icon;

  const primaryButton = (
    <BuiButton
      type={type}
      testid={buttonAction["data-testid"]}
      onClick={buttonAction.onClick}
      disabled={(disabled ?? false) || loading}
      loading={loading}
      icon={buttonAction.icon}
      aria-label={buttonAction["aria-label"]}
      className={cn(className, { leftButton: hasMenu })}
    >
      {!shouldCollapse && buttonAction.content}
    </BuiButton>
  );

  if (!hasMenu) {
    return primaryButton;
  }

  const actions = menuItems.map((x) => (
    <BuiMenu.Item
      key={x["data-testid"]}
      testid={x["data-testid"]}
      onClick={x.onClick}
      disabled={x.disabled ?? false}
      aria-label={x["aria-label"]}
    >
      {x.content}
    </BuiMenu.Item>
  ));

  return (
    <div className="BuiSplitButton" data-testid={testid}>
      {primaryButton}
      <BuiDropdown
        testid={`${testid}-split-button-dropdown`}
        triggerRef={triggerRef}
        placement={placement}
        disabled={(disabled ?? false) || loading}
        {...(getPopupContainer ? { getPopupContainer } : {})}
        overlay={
          <BuiMenu
            aria-label="Related actions menu"
            testid={`${testid}-splitButtonMenu`}
          >
            {actions}
          </BuiMenu>
        }
      >
        <BuiButton
          ref={triggerRef}
          type={type}
          testid={`${testid}-rightButton`}
          aria-label="Related actions"
          disabled={(disabled ?? false) || loading}
          className={cn(className, "rightButton")}
          icon={<IconCaretSmallDown />}
        />
      </BuiDropdown>
    </div>
  );
};
